/**
 * `/my_work` (M5.C3, `specs/M7-C3-inbox-my-work.md` §10).
 *
 * Drawn to the geometry measured on monday (`discovery/ui-reference/my-work/01-anatomy.json`):
 * a 32 px/500 title at (103, 119), `Customize` 112 × 32 at (1299, 123), the `Table | Calendar`
 * strip 169 × 32 at (103, 175), a toolbar at y 216 and, per bucket, an 18 px/500 header with a
 * count pill, a 34 px column strip and **78 px** item rows 1327 wide.
 *
 * The Calendar tab renders a `NotAvailableYet` body rather than disappearing, exactly as M5's
 * inbox does for `All account`, so the strip keeps the widths the parity fixture asserts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnchoredPopover, Avatar, PageEmptyState, formatMondayDayMonth, formatMondayTime } from '@elizmonday/ui';
import {
  MY_WORK_GROUP_BY,
  MY_WORK_GROUP_BY_LABELS,
  type MyWorkGroupById,
  type MyWorkResultView,
  type MyWorkRowView,
  type MyWorkSource,
} from './types';
import { CustomizeDrawer } from './CustomizeDrawer';

export interface MyWorkPageProps {
  source: MyWorkSource;
  /** Injected so the page works in the fixture harness with no router. */
  onOpenItem?: (row: MyWorkRowView) => void;
}

/** The four column headers monday renders, at their measured widths. */
const COLUMNS: { key: 'date' | 'status' | 'priority' | 'people'; label: string; width: number; sortable: boolean }[] = [
  { key: 'date', label: 'Date', width: 156, sortable: true },
  { key: 'status', label: 'Status', width: 156, sortable: false },
  { key: 'priority', label: 'Priority', width: 156, sortable: false },
  { key: 'people', label: 'People', width: 130, sortable: false },
];

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const day = formatMondayDayMonth(date);
  const midnight = date.getUTCHours() === 0 && date.getUTCMinutes() === 0;
  if (midnight) return day;
  return `${day}, ${formatMondayTime(date)}`;
}

export function MyWorkPage({ source, onOpenItem }: MyWorkPageProps) {
  const [tab, setTab] = useState<'table' | 'calendar'>('table');
  const [query, setQuery] = useState('');
  const [data, setData] = useState<MyWorkResultView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);
  /**
   * P-017: the popover measures itself against the **button**, not its wrapper — the wrapper is
   * a block whose line box is a few pixels taller, and the captured menu sits exactly 4 px under
   * the button's 32 px bottom edge.
   */
  const groupMenuAnchor = useRef<HTMLButtonElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [revision, setRevision] = useState(0);

  const load = useCallback(
    (patch: { groupBy?: MyWorkGroupById; sort?: 'date_asc' | 'date_desc'; hideDone?: boolean } = {}) => {
      setLoading(true);
      source
        .list({ ...(query.trim() ? { query: query.trim() } : {}), ...patch })
        .then((next) => {
          setData(next);
          setError(null);
        })
        .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Could not load My Work'))
        .finally(() => setLoading(false));
    },
    [source, query],
  );

  useEffect(() => {
    load();
  }, [load, revision]);

  const settings = data?.settings;
  const groupBy = settings?.groupBy ?? 'date';

  const toggleSort = () => {
    const next = settings?.sort === 'date_asc' ? 'date_desc' : 'date_asc';
    void source.saveSettings({ sort: next }).then(() => setRevision((value) => value + 1));
  };

  const chooseGroupBy = (next: MyWorkGroupById) => {
    setGroupMenuOpen(false);
    void source.saveSettings({ groupBy: next }).then(() => setRevision((value) => value + 1));
  };

  const sections = useMemo(() => data?.sections ?? [], [data]);
  const totalRows = sections.reduce((sum, section) => sum + section.count, 0);

  return (
    <div className="ez-mywork" data-testid="my-work-page">
      <header className="ez-mywork__header">
        <h1 className="ez-mywork__title" data-testid="my-work-title">
          My Work
        </h1>
        <button
          type="button"
          className="ez-mywork__customize"
          data-testid="my-work-customize"
          onClick={() => setDrawerOpen(true)}
        >
          Customize
        </button>
      </header>

      <div className="ez-mywork__tabs" role="tablist" aria-label="My Work views" data-testid="my-work-tabs">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'table'}
          className={`ez-mywork__tab${tab === 'table' ? ' is-selected' : ''}`}
          data-testid="my-work-tab-table"
          onClick={() => setTab('table')}
        >
          Table
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'calendar'}
          className={`ez-mywork__tab is-calendar${tab === 'calendar' ? ' is-selected' : ''}`}
          data-testid="my-work-tab-calendar"
          onClick={() => setTab('calendar')}
        >
          Calendar
        </button>
      </div>

      <div className="ez-mywork__toolbar">
        {/*
          monday's `New item` needs a board + group picker we have not captured; the control is
          rendered at its measured size and disabled rather than invented (spec D-4).
        */}
        <button type="button" className="ez-mywork__newitem" data-testid="my-work-new-item" disabled>
          New item
        </button>
        <input
          type="search"
          className="ez-mywork__filter"
          value={query}
          placeholder="Search"
          aria-label="Filter dashboard by text"
          data-testid="my-work-filter"
          onChange={(event) => setQuery(event.target.value)}
        />
        {/*
          P-017: the menu used to be an inline `position: absolute` box. `--ez-layer-popover` on
          it meant nothing — it joined the toolbar's stacking context, and on a phone the calendar
          strip below painted **over** it: `elementFromPoint` at the menu's own centre returned an
          SVG `<rect>` of `my-work-calendar`. `AnchoredPopover` portals it to `<body>` on the one
          layer scale, which is the fix P-005 established for the shell.
        */}
        <div className="ez-mywork__groupby">
          <button
            ref={groupMenuAnchor}
            type="button"
            className="ez-mywork__groupby-button"
            aria-haspopup="menu"
            aria-expanded={groupMenuOpen}
            data-testid="my-work-groupby"
            onClick={() => setGroupMenuOpen((open) => !open)}
          >
            {MY_WORK_GROUP_BY_LABELS[groupBy]}
          </button>
          <AnchoredPopover
            anchor={groupMenuAnchor.current}
            open={groupMenuOpen}
            onClose={() => setGroupMenuOpen(false)}
            label="Group by"
            role="menu"
            align="end"
            /* Measured 200 × 160, on the button's bottom edge (`verify-collab-local.mjs`). */
            gap={4}
            width={200}
            data-testid="my-work-groupby-menu"
          >
            <div className="ez-mywork__groupby-menu">
              {MY_WORK_GROUP_BY.map((id) => (
                <button
                  key={id}
                  type="button"
                  role="menuitem"
                  aria-checked={groupBy === id}
                  className={groupBy === id ? 'is-selected' : undefined}
                  data-testid={`my-work-groupby-${id}`}
                  onClick={() => chooseGroupBy(id)}
                >
                  {MY_WORK_GROUP_BY_LABELS[id]}
                </button>
              ))}
            </div>
          </AnchoredPopover>
        </div>
      </div>

      {tab === 'calendar' ? (
        <PageEmptyState
          title="Calendar is not available yet"
          hint="My Work's Calendar tab arrives with M4.V4. The Table tab is complete."
          data-testid="my-work-calendar-empty"
        />
      ) : error ? (
        <PageEmptyState title="Could not load My Work" hint={error} data-testid="my-work-error" />
      ) : !loading && totalRows === 0 && query.trim().length === 0 ? (
        <PageEmptyState
          title="Nothing is assigned to you yet"
          hint="Items where you are in a people column — or that you created — show up here, grouped by date."
          data-testid="my-work-empty"
        />
      ) : (
        <div className="ez-mywork__sections">
          {sections.map((section) => {
            const isCollapsed = collapsed[section.key] === true;
            return (
              <section key={section.key} className="ez-mywork__section" data-testid={`my-work-section-${section.key}`}>
                <div className="ez-mywork__section-head">
                  <button
                    type="button"
                    className="ez-mywork__collapse"
                    aria-label="Collapse all groups"
                    aria-expanded={!isCollapsed}
                    data-testid={`my-work-collapse-${section.key}`}
                    onClick={() =>
                      setCollapsed((current) => ({ ...current, [section.key]: !isCollapsed }))
                    }
                  >
                    {isCollapsed ? '▸' : '▾'}
                  </button>
                  <span className="ez-mywork__section-label">{section.label}</span>
                  <span className="ez-mywork__section-count" data-testid={`my-work-count-${section.key}`}>
                    {section.count} {section.count === 1 ? 'item' : 'items'}
                  </span>
                  {/*
                    monday prints an empty bucket as its header alone — no column strip and no
                    `+ Add item` (`Today\n0 items\nThis week\n0 items`, back to back, in the
                    capture's text dump). Not rendered rather than `hidden`: a hidden strip is
                    still in the DOM, and the verifier counted its sort control.
                  */}
                  {section.rows.length === 0 ? null : (
                  <div className="ez-mywork__colstrip" aria-hidden={isCollapsed}>
                    {COLUMNS.map((column) => (
                      <div
                        key={column.key}
                        className={`ez-mywork__colhead${column.key === 'date' ? ' is-first' : ''}`}
                        style={{ width: column.width }}
                        data-testid={`my-work-colhead-${column.key}`}
                      >
                        <span>{column.label}</span>
                        {column.sortable ? (
                          <button
                            type="button"
                            className="ez-mywork__sort"
                            aria-label="Sort"
                            data-testid="my-work-sort-date"
                            onClick={toggleSort}
                          >
                            {settings?.sort === 'date_desc' ? '↓' : '↑'}
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                  )}
                </div>

                {isCollapsed || section.rows.length === 0 ? null : (
                  <div className="ez-mywork__rows">
                    {section.rows.map((row) => (
                      <div
                        key={row.itemId}
                        className="ez-mywork__row"
                        role="button"
                        tabIndex={0}
                        data-testid={`my-work-row-${row.itemId}`}
                        onClick={() => onOpenItem?.(row)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') onOpenItem?.(row);
                        }}
                      >
                        <div className="ez-mywork__row-main">
                          <nav className="ez-mywork__crumbs" aria-label="Breadcrumb">
                            <span className="ez-mywork__crumb">{row.boardName}</span>
                            <span className="ez-mywork__crumb">{row.groupTitle}</span>
                          </nav>
                          <span className="ez-mywork__name">{row.name}</span>
                        </div>
                        <button
                          type="button"
                          className="ez-mywork__bubble"
                          aria-label="Start conversation"
                          data-testid={`my-work-bubble-${row.itemId}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpenItem?.(row);
                          }}
                        >
                          💬
                          {row.unreadUpdateCount > 0 ? <i className="ez-mywork__dot" aria-hidden="true" /> : null}
                        </button>
                        <div className="ez-mywork__cell" style={{ width: 156 }}>
                          {formatDate(row.date)}
                        </div>
                        <div className="ez-mywork__cell" style={{ width: 156 }}>
                          {row.status ? (
                            <span
                              className="ez-mywork__pill"
                              style={row.status.color ? { background: row.status.color } : undefined}
                            >
                              {row.status.text}
                            </span>
                          ) : null}
                        </div>
                        <div className="ez-mywork__cell" style={{ width: 156 }}>
                          {row.priority ? (
                            <span
                              className="ez-mywork__pill"
                              style={row.priority.color ? { background: row.priority.color } : undefined}
                            >
                              {row.priority.text}
                            </span>
                          ) : null}
                        </div>
                        <div className="ez-mywork__cell ez-mywork__cell--people" style={{ width: 130 }}>
                          {row.people.slice(0, 3).map((person) => (
                            <Avatar
                              key={person.id}
                              size="small"
                              type={person.photoUrl ? 'img' : 'text'}
                              src={person.photoUrl ?? undefined}
                              text={(person.name ?? '?').slice(0, 2).toUpperCase()}
                              aria-label={person.name ?? 'Person'}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                    <div className="ez-mywork__additem" data-testid={`my-work-additem-${section.key}`}>
                      + Add item
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {drawerOpen && settings ? (
        <CustomizeDrawer
          settings={settings}
          boards={data?.boards ?? []}
          onClose={() => setDrawerOpen(false)}
          onSave={(patch) => {
            void source.saveSettings(patch).then(() => setRevision((value) => value + 1));
          }}
        />
      ) : null}
    </div>
  );
}
